import { shrinkImage } from "../api/upload";

/**
 * UNE CAPTURE D'ÉCRAN JOINTE À UNE DEMANDE D'APPARENCE.
 *
 * « Ce titre-là, comme ça » se montre mieux qu'il ne se décrit. L'organisateur joint une
 * capture — l'état actuel annoté, ou le résultat qu'il veut — et le modèle la voit
 * avant la consigne (`POST /gpt/generate-css`, voir `message.js` côté API).
 *
 * ⚠️ ELLE NE VA NULLE PART. Pas de seau, pas d'adresse : elle part en base64 dans le
 * corps de la demande et n'est conservée par personne. Une capture n'a de sens que
 * pour la demande qu'elle accompagne.
 *
 * ⚠️ RÉDUITE AVANT DE PARTIR. Un écran Retina fait 3 000 px de large et un PNG de
 * capture pèse plusieurs mégaoctets : inutile pour lire une couleur ou une taille, et
 * trop pour le corps d'une Lambda (6 Mo). `shrinkImage` la ramène à 1 600 px en JPEG,
 * quelques centaines de kilo-octets. Le serveur garde son propre plafond au cas où le
 * navigateur n'aurait pas su la réduire.
 */

export type Capture = {
  /** Le type que porte le base64 (`image/jpeg` après réduction). */
  mediaType: string;
  /** Le base64 nu, sans préfixe `data:`. */
  data: string;
  /** Une adresse locale pour la vignette ; à libérer avec `releaseCapture`. */
  previewUrl: string;
};

/** Le plus grand côté envoyé, en pixels : assez pour lire un écran, pas pour l'archiver. */
export const CAPTURE_MAX_SIDE = 1600;
/** JPEG moins fin qu'un portrait : une capture a des aplats, pas une peau. */
export const CAPTURE_QUALITY = 0.8;
/** Ce qu'on accepte de LIRE ; ce qui part est réduit. */
export const CAPTURE_MAX_BYTES = 12 * 1024 * 1024;

export class CaptureError extends Error {}

/**
 * Sépare une adresse `data:` en son type et son base64. Le type est ce qui est écrit,
 * pas ce que dit le fichier : après `shrinkImage`, c'est `image/jpeg`.
 */
export function splitDataUrl(url: string): { mediaType: string; data: string } {
  const match = /^data:([^;,]+)(?:;[^,]*)?;base64,(.*)$/s.exec(url);
  const mediaType = match?.[1];
  const data = match?.[2];
  if (!mediaType || data === undefined) throw new CaptureError("Capture illisible.");
  return { mediaType: mediaType.toLowerCase(), data };
}

/**
 * L'image d'un collage (Ctrl+V), s'il y en a une. Un presse-papiers porte souvent
 * plusieurs formes du même contenu (texte + image) : la première image gagne, le
 * texte est ignoré — c'est le champ de consigne qui le recevra, lui.
 */
export function imageFromClipboard(
  items: ArrayLike<{ kind: string; type: string; getAsFile(): File | null }> | null | undefined,
): File | null {
  if (!items) return null;
  for (let i = 0; i < items.length; i += 1) {
    const item = items[i];
    if (item && item.kind === "file" && item.type.startsWith("image/")) {
      const file = item.getAsFile();
      if (file) return file;
    }
  }
  return null;
}

function readAsDataUrl(blob: Blob): Promise<string> {
  return new Promise((done, fail) => {
    const reader = new FileReader();
    reader.onload = () => done(String(reader.result || ""));
    reader.onerror = () => fail(new CaptureError("Capture illisible."));
    reader.readAsDataURL(blob);
  });
}

/** Lit, réduit et encode une capture, prête à partir avec la demande. */
export async function readCapture(file: File): Promise<Capture> {
  if (!file.type.startsWith("image/")) {
    throw new CaptureError("Ce fichier n'est pas une image.");
  }
  if (file.size > CAPTURE_MAX_BYTES) {
    const mo = Math.round((file.size / 1024 / 1024) * 10) / 10;
    throw new CaptureError(`Capture trop lourde (${mo} Mo, maximum 12 Mo).`);
  }
  const small = await shrinkImage(file, CAPTURE_MAX_SIDE, CAPTURE_QUALITY);
  const { mediaType, data } = splitDataUrl(await readAsDataUrl(small));
  return { mediaType, data, previewUrl: URL.createObjectURL(small) };
}

/** Libère la vignette d'une capture qu'on ne montre plus. */
export function releaseCapture(capture: Capture | null): void {
  if (capture) {
    try {
      URL.revokeObjectURL(capture.previewUrl);
    } catch {
      /* déjà libérée */
    }
  }
}
